import { useEffect, useRef } from "react";
import { Loader2, CheckCircle, AlertCircle, Clock } from "lucide-react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useScheduleGeneration } from "../hooks/useScheduleGeneration";

interface ScheduleGenerationStatusProps {
  make: string;
  model: string;
  year: number;
  vehicleId: string;
}

function ConfidenceBadge({ score }: { score: number }) {
  const percentage = Math.round(score * 100);
  const color =
    percentage >= 80 ? "bg-green-100 text-green-700" : percentage >= 60 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700";

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {percentage}% confidence
    </span>
  );
}

const PASSTHROUGH_MESSAGES = [
  "only supported for Honda",
  "Schedule generation timed out",
  "Unable to check generation status",
];

export function getFriendlyErrorMessage(raw: string): string {
  if (PASSTHROUGH_MESSAGES.some((msg) => raw.includes(msg))) {
    return raw;
  }

  const lower = raw.toLowerCase();

  if (lower.includes("api_key") || lower.includes("api key") || lower.includes("not configured")) {
    return "Our AI service isn't available right now. Please try again later.";
  }
  if (lower.includes("credit") || lower.includes("quota") || lower.includes("billing") || lower.includes("insufficient")) {
    return "Our AI service is temporarily at capacity. Please try again in a little while.";
  }
  if (lower.includes("rate limit") || lower.includes("429") || lower.includes("too many requests")) {
    return "We're handling a lot of requests right now. Please try again in a few minutes.";
  }
  if (lower.includes("timeout") || lower.includes("timed out")) {
    return "The request took too long — please try again.";
  }

  return "Something went wrong generating your schedule. Please try again, and if the issue persists, check back later.";
}

export function ScheduleGenerationStatus({
  make,
  model,
  year,
  vehicleId,
}: ScheduleGenerationStatusProps) {
  const { status, confidenceScore, sourcesUsed, errorMessage, retry } = useScheduleGeneration(
    make,
    model,
    year
  );

  const prevStatusRef = useRef(status);
  useEffect(() => {
    if (prevStatusRef.current === "pending" && status === "completed") {
      toast.success("Maintenance schedule is ready! View it now.");
    }
    prevStatusRef.current = status;
  }, [status]);

  if (!status) return null;

  if (status === "pending") {
    return (
      <div className="mt-4 bg-blue-50 rounded-lg p-4 flex items-center gap-3">
        <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" role="img" aria-label="Generating" />
        <div>
          <p className="text-sm font-medium text-blue-900">Generating maintenance schedule...</p>
          <p className="text-xs text-blue-700 mt-0.5">This usually takes 30-60 seconds</p>
        </div>
      </div>
    );
  }

  if (status === "completed") {
    return (
      <div className="mt-4 bg-green-50 rounded-lg p-4 flex items-center gap-3">
        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" role="img" aria-label="Completed" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-green-900">Schedule ready!</p>
            {confidenceScore != null && <ConfidenceBadge score={confidenceScore} />}
          </div>
          {sourcesUsed && sourcesUsed.length > 0 && (
            <p className="text-xs text-green-700 mt-0.5">
              Sources: {sourcesUsed.join(", ")}
            </p>
          )}
          <Link
            to={`/schedules?vehicle=${vehicleId}`}
            className="text-xs text-green-700 hover:text-green-800 underline mt-0.5 inline-block"
          >
            View Schedule
          </Link>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="mt-4 bg-red-50 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" role="img" aria-label="Failed" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-900">Schedule generation failed</p>
          {errorMessage && <p className="text-xs text-red-700 mt-0.5">{getFriendlyErrorMessage(errorMessage)}</p>}
          <p className="text-xs text-red-600 mt-0.5">You can still log maintenance manually from the Maintenance page.</p>
        </div>
        <button
          onClick={retry}
          className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // needs_review
  return (
    <div className="mt-4 bg-yellow-50 rounded-lg p-4 flex items-center gap-3">
      <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0" role="img" aria-label="Awaiting review" />
      <div>
        <p className="text-sm font-medium text-yellow-900">Schedule is being reviewed</p>
        <p className="text-xs text-yellow-700 mt-0.5">
          A team member is verifying the generated schedule
        </p>
      </div>
    </div>
  );
}
